import { useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRxDatabase } from 'rxdb/plugins/react';
import { v4 as uuid } from 'uuid';
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    closestCenter,
    pointerWithin,
    useDroppable,
    useSensor,
    useSensors,
    type Active,
    type CollisionDetection,
    type DragEndEvent,
    type DragMoveEvent,
    type DragStartEvent,
    type Over
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    type SortingStrategy
} from '@dnd-kit/sortable';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GridOffIcon from '@mui/icons-material/GridOff';
import SaveIcon from '@mui/icons-material/Save';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import type { Theme } from '@mui/material/styles';
import {
    MEDIA_GROUP_MAX_PER_ROW,
    MEDIA_GROUP_MAX_ROWS,
    serializeMediaGroupLayout,
    type MediaDocType,
    type MediaGroupDocType,
    type MediaOwnerType
} from '@ast-data/shared';
import DialogFooterBar from '../../components/DialogFooterBar';
import FormDialog from '../../components/FormDialog';
import FormDialogAppBar from '../../components/FormDialogAppBar';
import FormSection, { FormSectionsBody } from '../../components/FormSection';
import { FormSectionNavContext } from '../../components/formSectionNavContext';
import { useToast } from '../../components/toastContext';
import type { AppDatabase } from '../../db/database';
import { downloadBlob } from '../../utils/downloadFile';
import { plateFileName } from './mediaFileName';
import MediaThumbnail from './MediaThumbnail';
import {
    fillRows,
    plateRows,
    suggestRowLengths,
    type ResolvedGroup
} from './mediaGroups';
import {
    plateGeometry,
    sheetPlateAt,
    type PlateRowGeometry
} from './plateGeometry';
import { PlateCaption, PlateImage } from './plateView';
import { useElementWidth, useMediaAspects } from './usePlateView';

/** The drop target of the single pictures, those on no plate. */
const POOL = 'pool';
const ROW_PREFIX = 'row-';
/** A folded plate's band: a picture dropped on it joins its last row. */
const HEAD_PREFIX = 'head-';
const POOL_TILE_WIDTH = 104;
const POOL_TILE_HEIGHT = 78;
/** How tall an empty row stands, so there is something to drop onto. */
const EMPTY_ROW_HEIGHT = 88;
/** The insertion mark between two pictures. */
const MARKER_WIDTH = 3;

interface EditorRow {
    /** Stable across moves, so a row keeps its drop target while dragged over. */
    key: string;
    ids: string[];
}

interface EditorPlate {
    /** The stored plate's id, or a fresh one for a plate not saved yet. */
    id: string;
    /** As last saved; absent for a new plate. */
    stored?: MediaGroupDocType;
    caption: string;
    rows: EditorRow[];
    open: boolean;
}

/** Where a dragged picture would go if it were let go now. */
interface DropTarget {
    /** POOL, a row's key or a folded plate's band. */
    container: string;
    /** In a row: the place it would take. */
    index: number;
    /** The row or plate is full; letting go there does nothing. */
    refused: boolean;
}

/** How a drop target is marked: taken, or full. */
type Highlight = 'accept' | 'refuse' | null;

function newRow(ids: string[] = []): EditorRow {
    return { key: `${ROW_PREFIX}${uuid()}`, ids };
}

function isContainer(id: string): boolean {
    return (
        id === POOL || id.startsWith(ROW_PREFIX) || id.startsWith(HEAD_PREFIX)
    );
}

/**
 * What lies under the POINTER. A row is as wide as the dialog, so a rule that
 * compares corners or centres (closestCorners) finds its far-off corners
 * beaten by the small tiles, and the picture lands anywhere but where it was
 * dropped. Under the pointer, a picture wins over the row or pool it sits in,
 * so it slots in beside that picture.
 *
 * Only the keyboard has no pointer; it moves by the nearest centre.
 */
const underPointer: CollisionDetection = (args) => {
    if (!args.pointerCoordinates) {
        return closestCenter(args);
    }
    const hits = pointerWithin(args);
    const pictures = hits.filter(({ id }) => !isContainer(String(id)));
    return pictures.length > 0 ? pictures : hits;
};

/**
 * Nothing moves aside while a picture is dragged: the plate reflows only once
 * it is let go. Moving the pictures along under the pointer made the rows
 * jump, and the target with them. Where it will land is MARKED instead.
 */
const STAND_STILL: SortingStrategy = () => null;

/**
 * The outline of a drop target, by what letting go there would do. An outline,
 * never a border: it takes no room, so marking a target moves nothing.
 * `tint` shades the target itself too — the row, not the whole plate.
 */
function highlightSx(highlight: Highlight, tint = true) {
    if (!highlight) {
        return {};
    }
    return {
        // Dashed, the way a drop zone is drawn everywhere else.
        outline: '2px dashed',
        outlineOffset: 2,
        outlineColor: (theme: Theme) =>
            highlight === 'accept'
                ? theme.palette.primary.main
                : theme.palette.error.main,
        ...(tint ? { bgcolor: 'action.hover' } : {})
    };
}

/**
 * Numbers the pictures as the sheet prints them: the single sketches, then the
 * single photos, each counted from one, and the plates carrying both counts
 * on in their order.
 */
function printedNumbers(
    singles: readonly MediaDocType[],
    plates: readonly EditorPlate[],
    byId: ReadonlyMap<string, MediaDocType>
): Map<string, number> {
    const counts = { sketch: 0, photo: 0 };
    const numbers = new Map<string, number>();
    const count = (media: MediaDocType | undefined) => {
        if (!media) {
            return;
        }
        const kind = media.kind === 'sketch' ? 'sketch' : 'photo';
        counts[kind] += 1;
        numbers.set(media.id, counts[kind]);
    };
    singles.filter((media) => media.kind === 'sketch').forEach(count);
    singles.filter((media) => media.kind !== 'sketch').forEach(count);
    for (const plate of plates) {
        plate.rows
            .flatMap((row) => row.ids)
            .forEach((id) => count(byId.get(id)));
    }
    return numbers;
}

/** Takes a picture off every plate: it is a single picture again. */
function withoutPicture(plates: EditorPlate[], id: string): EditorPlate[] {
    return plates.map((plate) => ({
        ...plate,
        rows: plate.rows.map((row) => ({
            ...row,
            ids: row.ids.filter((other) => other !== id)
        }))
    }));
}

/**
 * A picture at the end of a plate: its last row with room, else a new row.
 * Null where the plate is full.
 */
function appendedTo(plate: EditorPlate, id: string): EditorPlate | null {
    const last = plate.rows[plate.rows.length - 1];
    if (last && last.ids.length < MEDIA_GROUP_MAX_PER_ROW) {
        return {
            ...plate,
            rows: plate.rows.map((row) =>
                row === last ? { ...row, ids: [...row.ids, id] } : row
            )
        };
    }
    if (plate.rows.length >= MEDIA_GROUP_MAX_ROWS) {
        return null;
    }
    return { ...plate, rows: [...plate.rows, newRow([id])] };
}

/** The horizontal middle of a dragged or targeted element. */
function middleX(rect: { left: number; width: number } | null | undefined) {
    return rect ? rect.left + rect.width / 2 : 0;
}

/**
 * How the pictures of one description stand in its PDF, and where that is
 * arranged: the single pictures on top, the plates (Bildtafeln) below, each
 * folded away to one line or open to its rows. Pictures are dragged from the
 * singles into a plate's row, along a row, between rows and plates and back;
 * a tap on a single picture puts it on the plate last opened.
 *
 * Rows are drawn as the PDF sets them — every row filling the width at its
 * pictures' own proportions — so the arrangement on screen is the printed one.
 * Nothing is written until Speichern.
 */
export default function MediaLayoutDialog({
    onClose,
    projectId,
    ownerType,
    ownerId,
    placeLabel,
    namePrefix,
    pictures,
    groups
}: {
    onClose: () => void;
    projectId: string;
    ownerType: MediaOwnerType;
    ownerId: string;
    /** Where the pictures hang, e.g. "Bef. 7000, Fl. 1, Pl. 1" — for the bar. */
    placeLabel?: string;
    /** Maßnahme + Lage, which a downloaded plate is named after. */
    namePrefix: string;
    /** Every picture of the description, in the gallery's order. */
    pictures: readonly MediaDocType[];
    groups: readonly ResolvedGroup<MediaDocType>[];
}) {
    const intl = useIntl();
    const { notify } = useToast();
    const database = useRxDatabase() as unknown as AppDatabase | null;
    const byId = new Map(pictures.map((item) => [item.id, item]));
    const [plates, setPlates] = useState<EditorPlate[]>(() =>
        groups.map((entry) => ({
            id: entry.group.id,
            stored: entry.group,
            caption: entry.group.caption,
            rows: plateRows(entry.group, entry.members, (item) => item.id).map(
                (row) => newRow(row.map((item) => item.id))
            ),
            // One plate is worked on; several are an overview first.
            open: groups.length === 1
        }))
    );
    const [removed, setRemoved] = useState<MediaGroupDocType[]>([]);
    // Where a tapped single picture goes: the plate last opened or made.
    const [focusId, setFocusId] = useState<string | null>(
        groups.length === 1 ? groups[0].group.id : null
    );
    const [activeId, setActiveId] = useState<string | null>(null);
    const [target, setTarget] = useState<DropTarget | null>(null);
    const [saving, setSaving] = useState(false);
    // The plate being drawn into a JPEG, while one is.
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const { ref: platesRef, width } = useElementWidth<HTMLDivElement>();
    const { aspectOf, report } = useMediaAspects();

    const placed = new Set(
        plates.flatMap((plate) => plate.rows.flatMap((row) => row.ids))
    );
    const singles = pictures.filter((item) => !placed.has(item.id));
    const numbers = printedNumbers(singles, plates, byId);
    const allRows = plates.flatMap((plate) => plate.rows);

    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        // A short hold on a tablet, so a swipe still scrolls the dialog.
        useSensor(TouchSensor, {
            activationConstraint: { delay: 200, tolerance: 6 }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    /** The row (or the singles) holding a picture, or named by a target. */
    function containerOf(id: string): string | null {
        if (isContainer(id)) {
            return id;
        }
        const row = allRows.find((candidate) => candidate.ids.includes(id));
        if (row) {
            return row.key;
        }
        return singles.some((item) => item.id === id) ? POOL : null;
    }

    /**
     * Where letting go now would put the picture, or null where it would stay
     * as it is. Over a picture it goes before or after it, by which half of it
     * the dragged picture's middle is in.
     */
    function dropTargetOf(
        active: Active,
        over: Over | null
    ): DropTarget | null {
        const id = String(active.id);
        const overId = over ? String(over.id) : null;
        if (!overId || overId === id) {
            return null;
        }
        const container = containerOf(overId);
        if (!container) {
            return null;
        }
        if (container === POOL) {
            return placed.has(id)
                ? { container, index: 0, refused: false }
                : null;
        }
        if (container.startsWith(HEAD_PREFIX)) {
            const plate = plates.find(
                (candidate) =>
                    candidate.id === container.slice(HEAD_PREFIX.length)
            );
            if (!plate || plate.rows.some((row) => row.ids.includes(id))) {
                return null;
            }
            return { container, index: 0, refused: !appendedTo(plate, id) };
        }
        const row = allRows.find((candidate) => candidate.key === container);
        if (!row) {
            return null;
        }
        const overIndex = row.ids.indexOf(overId);
        const after =
            middleX(active.rect.current.translated) > middleX(over?.rect);
        return {
            container,
            index: overIndex < 0 ? row.ids.length : overIndex + (after ? 1 : 0),
            refused:
                !row.ids.includes(id) &&
                row.ids.length >= MEDIA_GROUP_MAX_PER_ROW
        };
    }

    function onDragStart({ active }: DragStartEvent) {
        setActiveId(String(active.id));
    }

    /** Only the mark moves while dragging — updated when the place changes. */
    function onDragMove({ active, over }: DragMoveEvent) {
        const next = dropTargetOf(active, over);
        setTarget((current) =>
            current?.container === next?.container &&
            current?.index === next?.index &&
            current?.refused === next?.refused
                ? current
                : next
        );
    }

    /** Letting go: the one moment the plate changes. */
    function onDragEnd({ active, over }: DragEndEvent) {
        const drop = dropTargetOf(active, over);
        setActiveId(null);
        setTarget(null);
        if (!drop || drop.refused) {
            return;
        }
        const id = String(active.id);
        if (drop.container === POOL) {
            setPlates((current) => withoutPicture(current, id));
            return;
        }
        if (drop.container.startsWith(HEAD_PREFIX)) {
            const plateId = drop.container.slice(HEAD_PREFIX.length);
            setPlates((current) => {
                const rest = withoutPicture(current, id);
                return rest.map((plate) =>
                    plate.id === plateId
                        ? (appendedTo(plate, id) ?? plate)
                        : plate
                );
            });
            return;
        }
        setPlates((current) =>
            current.map((plate) => ({
                ...plate,
                rows: plate.rows.map((row) => {
                    const from = row.ids.indexOf(id);
                    if (row.key !== drop.container) {
                        return from < 0
                            ? row
                            : {
                                  ...row,
                                  ids: row.ids.filter((other) => other !== id)
                              };
                    }
                    const ids = row.ids.filter((other) => other !== id);
                    // Taken out ahead of its new place, it shifts it by one.
                    const at =
                        from >= 0 && from < drop.index
                            ? drop.index - 1
                            : drop.index;
                    ids.splice(at, 0, id);
                    return { ...row, ids };
                })
            }))
        );
    }

    function onDragCancel() {
        setActiveId(null);
        setTarget(null);
    }

    function updatePlate(
        plateId: string,
        change: (plate: EditorPlate) => EditorPlate
    ) {
        setPlates((current) =>
            current.map((plate) =>
                plate.id === plateId ? change(plate) : plate
            )
        );
    }

    function toggle(plate: EditorPlate) {
        updatePlate(plate.id, (current) => ({
            ...current,
            open: !current.open
        }));
        if (!plate.open) {
            setFocusId(plate.id);
        }
    }

    function addPlate() {
        const id = uuid();
        setPlates((current) => [
            ...current,
            { id, caption: '', rows: [newRow()], open: true }
        ]);
        setFocusId(id);
    }

    /** A plate one place up or down: the order the PDF prints them in. */
    function movePlate(index: number, offset: number) {
        setPlates((current) => arrayMove(current, index, index + offset));
    }

    /** The plate goes; its pictures are single pictures again. */
    function removePlate(plate: EditorPlate) {
        setPlates((current) =>
            current.filter((candidate) => candidate.id !== plate.id)
        );
        const stored = plate.stored;
        if (stored) {
            setRemoved((current) => [...current, stored]);
        }
    }

    /** A tap on a single picture: onto the plate last opened, else a new one. */
    function place(id: string) {
        const focused = plates.find((plate) => plate.id === focusId);
        const next = focused ? appendedTo(focused, id) : null;
        if (focused && next) {
            updatePlate(focused.id, () => ({ ...next, open: true }));
            return;
        }
        const plateId = uuid();
        setPlates((current) => [
            ...current,
            { id: plateId, caption: '', rows: [newRow([id])], open: true }
        ]);
        setFocusId(plateId);
    }

    /**
     * One plate as a JPEG, as it stands in the dialog now — saved or not — and
     * set exactly as its PDF sheet prints it.
     */
    async function downloadPlate(plate: EditorPlate, plateNumber: number) {
        const rows = plate.rows
            .map((row) => row.ids)
            .filter((ids) => ids.length > 0);
        const members = rows.flat().flatMap((id) => {
            const media = byId.get(id);
            return media ? [{ media, number: numbers.get(id) ?? 0 }] : [];
        });
        const now = new Date().toISOString();
        const group: MediaGroupDocType = {
            ...(plate.stored ?? {
                id: plate.id,
                projectId,
                ownerType,
                ownerId,
                sortOrder: plateNumber - 1,
                createdBy: '',
                updatedBy: '',
                updatedAt: now,
                createdAt: now
            }),
            caption: plate.caption.trim(),
            layout: serializeMediaGroupLayout(rows)
        };
        setDownloadingId(plate.id);
        try {
            // The Nordpfeil line of the Legende, as the Planum sheet has it.
            const planum =
                ownerType === 'findingPlanum'
                    ? await database?.finding_planums.findOne(ownerId).exec()
                    : null;
            const { renderPlateJpeg } =
                await import('../export/findingPdf/plateJpeg');
            const blob = await renderPlateJpeg({
                group,
                members,
                plateNumber,
                northAngle: planum?.northArrow ? planum.northAngle : null,
                translate: (id, values) => intl.formatMessage({ id }, values)
            });
            downloadBlob(
                blob,
                plateFileName(
                    namePrefix,
                    intl.formatMessage(
                        { id: 'findingPdf.h.group' },
                        { n: plateNumber }
                    ),
                    group.caption
                )
            );
        } catch (cause) {
            const missing =
                cause instanceof Error &&
                cause.name === 'PlateWithoutImagesError';
            notify(
                intl.formatMessage({
                    id: missing
                        ? 'media.group.downloadNoImages'
                        : 'media.group.downloadFailed'
                }),
                'error'
            );
            if (!missing) {
                console.error('Drawing the plate as a JPEG failed', cause);
            }
        } finally {
            setDownloadingId(null);
        }
    }

    async function save() {
        if (!database) {
            return;
        }
        setSaving(true);
        try {
            const now = new Date().toISOString();
            const collection = database.media_groups;
            // The dialog's order is the printed order: each plate is given its
            // place, and the picture numbers follow it.
            for (const [sortOrder, plate] of plates.entries()) {
                const layout = serializeMediaGroupLayout(
                    plate.rows.map((row) => row.ids)
                );
                const caption = plate.caption.trim();
                const empty = !plate.rows.some((row) => row.ids.length > 0);
                if (!plate.stored) {
                    if (!empty) {
                        // An upsert: after a save that failed half-way, the
                        // retry finds this plate already written.
                        await collection.upsert({
                            id: plate.id,
                            projectId,
                            ownerType,
                            ownerId,
                            caption,
                            layout,
                            sortOrder,
                            createdBy: '',
                            updatedBy: '',
                            updatedAt: now,
                            createdAt: now
                        });
                    }
                    continue;
                }
                const live = await collection.findOne(plate.id).exec();
                if (empty) {
                    await live?.remove();
                } else if (
                    layout !== plate.stored.layout ||
                    caption !== plate.stored.caption ||
                    sortOrder !== plate.stored.sortOrder
                ) {
                    await live?.incrementalPatch({
                        caption,
                        layout,
                        sortOrder,
                        updatedAt: now
                    });
                }
            }
            // Removed last: should a write above fail, no plate is gone yet.
            for (const stored of removed) {
                await (await collection.findOne(stored.id).exec())?.remove();
            }
            onClose();
        } catch (cause) {
            // The dialog stays open with the arrangement as it was, so the
            // save can simply be tried again.
            notify(
                intl.formatMessage({ id: 'media.layout.saveFailed' }),
                'error'
            );
            console.error('Saving the picture layout failed', cause);
        } finally {
            setSaving(false);
        }
    }

    const highlightOf = (container: string): Highlight =>
        target?.container === container
            ? target.refused
                ? 'refuse'
                : 'accept'
            : null;
    // As the sheet prints it under the picture: "Foto 3 — Profil nach Osten".
    const labelOf = (media: MediaDocType): string => {
        const name = intl.formatMessage(
            {
                id:
                    media.kind === 'sketch'
                        ? 'findingPdf.h.sketch'
                        : 'findingPdf.h.photo'
            },
            { n: numbers.get(media.id) }
        );
        const caption = media.caption.trim();
        return caption ? `${name} — ${caption}` : name;
    };
    const unplaceLabel = intl.formatMessage({ id: 'media.group.unplace' });
    const active = activeId ? byId.get(activeId) : undefined;

    function renderPicture(
        row: EditorRow,
        cellIndex: number,
        cellWidth: number,
        height: number,
        gap: number
    ) {
        const id = row.ids[cellIndex];
        const media = byId.get(id);
        if (!media) {
            return null;
        }
        // The mark stands in the gap before this picture, or after the last.
        const marked =
            target?.container === row.key && !target.refused
                ? target.index === cellIndex
                    ? 'before'
                    : target.index === row.ids.length &&
                        cellIndex === row.ids.length - 1
                      ? 'after'
                      : null
                : null;
        return (
            <SortablePicture key={id} id={id}>
                {marked && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            height,
                            width: MARKER_WIDTH,
                            borderRadius: 1,
                            bgcolor: 'primary.main',
                            zIndex: 1,
                            [marked === 'before' ? 'left' : 'right']:
                                -(gap + MARKER_WIDTH) / 2
                        }}
                    />
                )}
                <PlateImage
                    media={media}
                    width={cellWidth}
                    height={height}
                    onAspect={report}
                >
                    <RemoveButton
                        label={unplaceLabel}
                        onClick={() =>
                            setPlates((current) => withoutPicture(current, id))
                        }
                    />
                </PlateImage>
                <PlateCaption width={cellWidth} label={labelOf(media)} wrap />
            </SortablePicture>
        );
    }

    return (
        <FormDialog open onClose={onClose} fullScreen>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                }}
            >
                {/* Named after the record it lays out: covering that record's
                    dialog, it would otherwise leave no trace of whose
                    pictures these are. */}
                <FormDialogAppBar
                    projectId={projectId}
                    onClose={onClose}
                    title={
                        placeLabel ? (
                            <>
                                {placeLabel} ›{' '}
                                <FormattedMessage id="media.layout.title" />
                            </>
                        ) : (
                            <FormattedMessage id="media.layout.title" />
                        )
                    }
                />
                {/* No section rail: the dialog opens over a record form whose
                    nav would otherwise reach in through the context. */}
                <FormSectionNavContext.Provider value={null}>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={underPointer}
                        onDragStart={onDragStart}
                        onDragMove={onDragMove}
                        onDragOver={onDragMove}
                        onDragEnd={onDragEnd}
                        onDragCancel={onDragCancel}
                    >
                        <FormSectionsBody>
                            <FormSection
                                title={
                                    <FormattedMessage id="media.layout.singles" />
                                }
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ color: 'text.secondary' }}
                                >
                                    <FormattedMessage id="media.layout.singlesHint" />
                                </Typography>
                                <PoolArea
                                    empty={singles.length === 0}
                                    highlight={highlightOf(POOL)}
                                >
                                    <SortableContext
                                        id={POOL}
                                        items={singles.map((item) => item.id)}
                                        strategy={STAND_STILL}
                                    >
                                        {singles.map((item) => (
                                            <SortablePicture
                                                key={item.id}
                                                id={item.id}
                                            >
                                                <Box
                                                    onClick={() =>
                                                        place(item.id)
                                                    }
                                                    sx={{
                                                        width: POOL_TILE_WIDTH,
                                                        cursor: 'pointer',
                                                        border: 1,
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <MediaThumbnail
                                                        media={item}
                                                        height={
                                                            POOL_TILE_HEIGHT
                                                        }
                                                    />
                                                </Box>
                                                <PlateCaption
                                                    width={POOL_TILE_WIDTH}
                                                    label={labelOf(item)}
                                                />
                                            </SortablePicture>
                                        ))}
                                    </SortableContext>
                                </PoolArea>
                            </FormSection>

                            <FormSection
                                title={
                                    <FormattedMessage id="media.layout.plates" />
                                }
                            >
                                {plates.length === 0 && (
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        <FormattedMessage id="media.layout.noPlates" />
                                    </Typography>
                                )}
                                <Stack ref={platesRef} spacing={1.5}>
                                    {width > 0 &&
                                        plates.map((plate, index) => (
                                            <PlatePanel
                                                key={plate.id}
                                                plate={plate}
                                                number={index + 1}
                                                width={width}
                                                targeted={
                                                    target !== null &&
                                                    !target.refused &&
                                                    (target.container ===
                                                        `${HEAD_PREFIX}${plate.id}` ||
                                                        plate.rows.some(
                                                            (row) =>
                                                                row.key ===
                                                                target.container
                                                        ))
                                                }
                                                highlightOf={highlightOf}
                                                aspectOf={(id) => {
                                                    const media = byId.get(id);
                                                    return media
                                                        ? aspectOf(media)
                                                        : null;
                                                }}
                                                captionOf={(id) => {
                                                    const media = byId.get(id);
                                                    return media
                                                        ? labelOf(media)
                                                        : '';
                                                }}
                                                onToggle={() => toggle(plate)}
                                                onCaption={(caption) =>
                                                    updatePlate(
                                                        plate.id,
                                                        (current) => ({
                                                            ...current,
                                                            caption
                                                        })
                                                    )
                                                }
                                                onRows={(rows) =>
                                                    updatePlate(
                                                        plate.id,
                                                        (current) => ({
                                                            ...current,
                                                            rows
                                                        })
                                                    )
                                                }
                                                onUp={
                                                    index > 0
                                                        ? () =>
                                                              movePlate(
                                                                  index,
                                                                  -1
                                                              )
                                                        : undefined
                                                }
                                                onDown={
                                                    index < plates.length - 1
                                                        ? () =>
                                                              movePlate(
                                                                  index,
                                                                  1
                                                              )
                                                        : undefined
                                                }
                                                onRemove={() =>
                                                    removePlate(plate)
                                                }
                                                downloading={
                                                    downloadingId === plate.id
                                                }
                                                onDownload={() =>
                                                    void downloadPlate(
                                                        plate,
                                                        index + 1
                                                    )
                                                }
                                                renderPicture={renderPicture}
                                            />
                                        ))}
                                </Stack>
                                <Box>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={addPlate}
                                    >
                                        <FormattedMessage id="media.group.new" />
                                    </Button>
                                </Box>
                            </FormSection>
                        </FormSectionsBody>
                        <DragOverlay>
                            {active && (
                                <Box
                                    sx={{
                                        width: POOL_TILE_WIDTH,
                                        boxShadow: 6,
                                        borderRadius: 1,
                                        overflow: 'hidden'
                                    }}
                                >
                                    <MediaThumbnail
                                        media={active}
                                        height={POOL_TILE_HEIGHT}
                                    />
                                </Box>
                            )}
                        </DragOverlay>
                    </DndContext>
                </FormSectionNavContext.Provider>
                <DialogFooterBar>
                    <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={onClose}
                    >
                        <FormattedMessage id="common.cancel" />
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={saving}
                        onClick={() => void save()}
                    >
                        <FormattedMessage id="common.save" />
                    </Button>
                </DialogFooterBar>
            </Box>
        </FormDialog>
    );
}

/**
 * One plate: a band that names it and folds it away, and, open, its title and
 * rows. Folded, the band itself takes a dropped picture onto the last row.
 */
function PlatePanel({
    plate,
    number,
    width,
    targeted,
    highlightOf,
    aspectOf,
    captionOf,
    onToggle,
    onCaption,
    onRows,
    onUp,
    onDown,
    onRemove,
    downloading,
    onDownload,
    renderPicture
}: {
    plate: EditorPlate;
    number: number;
    width: number;
    /** A picture would land on this plate if let go now. */
    targeted: boolean;
    highlightOf: (container: string) => Highlight;
    aspectOf: (id: string) => number | null;
    /** The line printed under a picture, which its row leaves room for. */
    captionOf: (id: string) => string;
    onToggle: () => void;
    onCaption: (caption: string) => void;
    onRows: (rows: EditorRow[]) => void;
    /** One place earlier among the plates; absent for the first. */
    onUp?: () => void;
    /** One place later; absent for the last. */
    onDown?: () => void;
    onRemove: () => void;
    /** The plate is being drawn into a JPEG. */
    downloading: boolean;
    onDownload: () => void;
    renderPicture: (
        row: EditorRow,
        cellIndex: number,
        width: number,
        height: number,
        gap: number
    ) => ReactNode;
}) {
    const intl = useIntl();
    const headId = `${HEAD_PREFIX}${plate.id}`;
    const { setNodeRef } = useDroppable({
        id: headId,
        disabled: plate.open
    });
    // The rows sit inside the panel's padding.
    const inner = Math.max(width - 26, 1);
    const options = sheetPlateAt(inner);
    const filled = plate.rows.filter((row) => row.ids.length > 0);
    const geometry = plateGeometry(
        filled.map((row) => row.ids.map(aspectOf)),
        options,
        filled.map((row) => row.ids.map(captionOf))
    );
    const count = filled.reduce((sum, row) => sum + row.ids.length, 0);
    const caption = plate.caption.trim();
    const headHighlight = highlightOf(headId);
    const toggleLabel = intl.formatMessage({
        id: plate.open ? 'media.group.collapse' : 'media.group.expand'
    });
    const removeLabel = intl.formatMessage({ id: 'media.group.remove' });
    const upLabel = intl.formatMessage({ id: 'media.group.plateUp' });
    const downLabel = intl.formatMessage({ id: 'media.group.plateDown' });
    const downloadLabel = intl.formatMessage({ id: 'media.group.download' });

    function moveRow(index: number, offset: number) {
        onRows(arrayMove(plate.rows, index, index + offset));
    }

    function removeRow(key: string) {
        const rest = plate.rows.filter((row) => row.key !== key);
        onRows(rest.length > 0 ? rest : [newRow()]);
    }

    function arrange() {
        const ids = plate.rows.flatMap((row) => row.ids);
        onRows(
            fillRows(ids, suggestRowLengths(ids.length)).map((row) =>
                newRow(row)
            )
        );
    }

    return (
        <Box
            sx={[
                { border: 1, borderColor: 'divider', borderRadius: 1 },
                // An outline over the border rather than a thicker border: it
                // takes no room, so marking the plate moves nothing.
                highlightSx(
                    headHighlight === 'refuse'
                        ? 'refuse'
                        : targeted
                          ? 'accept'
                          : null,
                    false
                )
            ]}
        >
            <Stack
                ref={setNodeRef}
                direction="row"
                spacing={1}
                onClick={onToggle}
                sx={{
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    cursor: 'pointer',
                    minWidth: 0,
                    bgcolor: headHighlight ? 'action.hover' : undefined
                }}
            >
                <IconButton
                    size="small"
                    aria-label={toggleLabel}
                    aria-expanded={plate.open}
                    onClick={(event) => {
                        event.stopPropagation();
                        onToggle();
                    }}
                    sx={{
                        transform: plate.open ? 'rotate(180deg)' : undefined,
                        transition: 'transform 150ms'
                    }}
                >
                    <ExpandMoreIcon fontSize="small" />
                </IconButton>
                <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{ flex: 1, minWidth: 0 }}
                >
                    <FormattedMessage
                        id="media.group.heading"
                        values={{ n: number }}
                    />
                    {caption && ` — ${caption}`}
                    <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: 'text.secondary', ml: 1 }}
                    >
                        <FormattedMessage
                            id="media.group.summary"
                            values={{
                                count,
                                rows: filled
                                    .map((row) => row.ids.length)
                                    .join(' + ')
                            }}
                        />
                    </Typography>
                </Typography>
                {/* Clicks here move or remove; they must not fold the plate,
                    not even on a disabled arrow. */}
                <Box
                    onClick={(event) => event.stopPropagation()}
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    <Tooltip title={upLabel}>
                        <span>
                            <IconButton
                                size="small"
                                aria-label={upLabel}
                                disabled={!onUp}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onUp?.();
                                }}
                            >
                                <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={downLabel}>
                        <span>
                            <IconButton
                                size="small"
                                aria-label={downLabel}
                                disabled={!onDown}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onDown?.();
                                }}
                            >
                                <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={downloadLabel}>
                        <span>
                            <IconButton
                                size="small"
                                aria-label={downloadLabel}
                                disabled={count === 0 || downloading}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onDownload();
                                }}
                            >
                                {downloading ? (
                                    <CircularProgress size={18} />
                                ) : (
                                    <DownloadIcon fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={removeLabel}>
                        <IconButton
                            size="small"
                            aria-label={removeLabel}
                            onClick={(event) => {
                                event.stopPropagation();
                                onRemove();
                            }}
                        >
                            <GridOffIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Stack>
            <Collapse in={plate.open} unmountOnExit>
                <Stack spacing={2} sx={{ p: 1.5, pt: 1 }}>
                    <TextField
                        size="small"
                        label={<FormattedMessage id="media.group.caption" />}
                        value={plate.caption}
                        onChange={(event) => onCaption(event.target.value)}
                        slotProps={{ htmlInput: { maxLength: 500 } }}
                        fullWidth
                    />
                    {plate.rows.map((row, index) => (
                        <PlateRow
                            key={row.key}
                            row={row}
                            number={index + 1}
                            gap={options.gap}
                            geometry={geometry.rows[filled.indexOf(row)]}
                            highlight={highlightOf(row.key)}
                            onUp={
                                index > 0 ? () => moveRow(index, -1) : undefined
                            }
                            onDown={
                                index < plate.rows.length - 1
                                    ? () => moveRow(index, 1)
                                    : undefined
                            }
                            onRemove={() => removeRow(row.key)}
                            renderPicture={renderPicture}
                        />
                    ))}
                    <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        sx={{ flexWrap: 'wrap' }}
                    >
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            disabled={plate.rows.length >= MEDIA_GROUP_MAX_ROWS}
                            onClick={() => onRows([...plate.rows, newRow()])}
                        >
                            <FormattedMessage id="media.group.addRow" />
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ViewQuiltIcon />}
                            disabled={count < 2}
                            onClick={arrange}
                        >
                            <FormattedMessage id="media.group.arrange" />
                        </Button>
                    </Stack>
                    {geometry.scale < 1 && (
                        <Alert severity="info">
                            <FormattedMessage id="media.group.shrunk" />
                        </Alert>
                    )}
                </Stack>
            </Collapse>
        </Box>
    );
}

/**
 * One row of a plate: a drop target along its whole length, even empty, and
 * its own moves — up, down, away (its pictures become single again).
 */
function PlateRow({
    row,
    number,
    gap,
    geometry,
    highlight,
    onUp,
    onDown,
    onRemove,
    renderPicture
}: {
    row: EditorRow;
    number: number;
    gap: number;
    /** This row's sizes; absent while it is empty. */
    geometry: PlateRowGeometry | undefined;
    highlight: Highlight;
    onUp?: () => void;
    onDown?: () => void;
    onRemove: () => void;
    renderPicture: (
        row: EditorRow,
        cellIndex: number,
        width: number,
        height: number,
        gap: number
    ) => ReactNode;
}) {
    const intl = useIntl();
    const { setNodeRef } = useDroppable({ id: row.key });
    const upLabel = intl.formatMessage({ id: 'media.group.rowUp' });
    const downLabel = intl.formatMessage({ id: 'media.group.rowDown' });
    const removeLabel = intl.formatMessage({ id: 'media.group.rowRemove' });
    const empty = row.ids.length === 0;
    return (
        <Box>
            <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: 'center', mb: 0.5 }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        flex: 1,
                        color:
                            highlight === 'refuse'
                                ? 'error.main'
                                : 'text.secondary'
                    }}
                >
                    <FormattedMessage
                        id={
                            highlight === 'refuse'
                                ? 'media.group.rowFull'
                                : 'media.group.row'
                        }
                        values={{
                            n: number,
                            count: row.ids.length,
                            max: MEDIA_GROUP_MAX_PER_ROW
                        }}
                    />
                </Typography>
                <Tooltip title={upLabel}>
                    <span>
                        <IconButton
                            size="small"
                            aria-label={upLabel}
                            disabled={!onUp}
                            onClick={onUp}
                        >
                            <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={downLabel}>
                    <span>
                        <IconButton
                            size="small"
                            aria-label={downLabel}
                            disabled={!onDown}
                            onClick={onDown}
                        >
                            <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={removeLabel}>
                    <IconButton
                        size="small"
                        aria-label={removeLabel}
                        onClick={onRemove}
                    >
                        <DeleteOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
            <SortableContext
                id={row.key}
                items={row.ids}
                strategy={STAND_STILL}
            >
                <Box
                    ref={setNodeRef}
                    sx={[
                        {
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: empty ? 'center' : 'flex-start',
                            gap: `${gap}px`,
                            minHeight: empty ? EMPTY_ROW_HEIGHT : undefined,
                            borderRadius: 1,
                            outline: empty ? '1px dashed' : 'none',
                            outlineOffset: 2,
                            outlineColor: 'divider'
                        },
                        highlightSx(highlight)
                    ]}
                >
                    {empty || !geometry ? (
                        <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary' }}
                        >
                            <FormattedMessage id="media.group.dropHere" />
                        </Typography>
                    ) : (
                        row.ids.map((_id, cellIndex) =>
                            renderPicture(
                                row,
                                cellIndex,
                                geometry.widths[cellIndex],
                                geometry.height,
                                gap
                            )
                        )
                    )}
                </Box>
            </SortableContext>
        </Box>
    );
}

/** The single pictures, a drop target too: dragged here, a picture leaves its plate. */
function PoolArea({
    empty,
    highlight,
    children
}: {
    empty: boolean;
    highlight: Highlight;
    children: ReactNode;
}) {
    const { setNodeRef } = useDroppable({ id: POOL });
    return (
        <Box
            ref={setNodeRef}
            sx={[
                {
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    minHeight: POOL_TILE_HEIGHT + 24,
                    p: 1,
                    borderRadius: 1,
                    outline: '1px dashed',
                    outlineColor: 'divider'
                },
                highlightSx(highlight)
            ]}
        >
            {empty ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <FormattedMessage id="media.layout.allGrouped" />
                </Typography>
            ) : (
                children
            )}
        </Box>
    );
}

/**
 * A picture that can be picked up. It stays where it is while dragged, faded;
 * the overlay follows the pointer.
 */
function SortablePicture({
    id,
    children
}: {
    id: string;
    children: ReactNode;
}) {
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
        id
    });
    return (
        <Box
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            sx={{
                position: 'relative',
                opacity: isDragging ? 0.35 : 1,
                cursor: 'grab',
                // The hold that starts a drag must not select the image.
                userSelect: 'none',
                WebkitTouchCallout: 'none'
            }}
        >
            {children}
        </Box>
    );
}

/** Takes a picture off its plate again. Its press never starts a drag. */
function RemoveButton({
    label,
    onClick
}: {
    label: string;
    onClick: () => void;
}) {
    return (
        <Tooltip title={label}>
            <IconButton
                size="small"
                aria-label={label}
                onPointerDown={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                onClick={onClick}
                sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    bgcolor: 'background.paper',
                    opacity: 0.9,
                    '&:hover': { bgcolor: 'background.paper', opacity: 1 }
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );
}
